import { useState, useEffect } from 'react'
import {
  ArrowLeft, Plus, CheckCircle, XCircle, Loader2, Eye, EyeOff,
  Pencil, Trash2, Signal, ChevronDown, ChevronUp, ArrowRightLeft,
} from 'lucide-react'
import type { ClientProfile } from '@isapi/shared'
import { useProxyConfig } from '@/hooks/useProxyConfig'
import { TotpVerifyModal } from '@/components/shared/TotpVerifyModal'
import {
  listProfiles, createProfile, updateProfile, deleteProfile,
  activateProfile, testProfile, relaunch,
} from '@/api/profiles'

interface ProviderModeProps {
  onBack: () => void
}

interface TestState {
  status: 'idle' | 'testing' | 'ok' | 'error'
  error?: string
  latencyMs?: number
}

interface ProfileForm {
  name: string
  databaseUrl: string
  showUrl: boolean
  testState: TestState
  saving: boolean
  error: string | null
  testedUrl: string | null
}

function emptyForm(): ProfileForm {
  return { name: '', databaseUrl: '', showUrl: false, testState: { status: 'idle' }, saving: false, error: null, testedUrl: null }
}

type MigrationEntry = { name: string; status: 'applied' | 'pending'; appliedAt?: string }
type MigrationRunEntry = { name: string; status: 'applied' | 'skipped' | 'failed'; error?: string }

export function ProviderMode({ onBack }: ProviderModeProps) {
  const { proxyUrl, proxyToken } = useProxyConfig()
  const headers: Record<string, string> = proxyToken ? { Authorization: `Bearer ${proxyToken}` } : {}

  const [profiles, setProfiles] = useState<ClientProfile[]>([])
  const [loading, setLoading] = useState(true)
  const [profileError, setProfileError] = useState<string | null>(null)

  const [confirmActivate, setConfirmActivate] = useState<ClientProfile | null>(null)
  const [activating, setActivating] = useState(false)
  const [confirmDelete, setConfirmDelete] = useState<ClientProfile | null>(null)
  const [deleting, setDeleting] = useState(false)
  const [testStates, setTestStates] = useState<Record<string, TestState>>({})

  const [addForm, setAddForm] = useState<ProfileForm | null>(null)
  const [editingId, setEditingId] = useState<string | null>(null)
  const [editForm, setEditForm] = useState<ProfileForm | null>(null)

  const [showMigrations, setShowMigrations] = useState(false)
  const [showTotpModal, setShowTotpModal] = useState(false)

  const [migrStatus, setMigrStatus] = useState<{ total: number; applied: number; pending: number; lastAppliedAt: string | null; migrations: MigrationEntry[] } | null>(null)
  const [migrLoading, setMigrLoading] = useState(false)
  const [runResults, setRunResults] = useState<MigrationRunEntry[] | null>(null)
  const [running, setRunning] = useState(false)
  const [runError, setRunError] = useState<string | null>(null)

  useEffect(() => { loadProfiles() }, [proxyUrl, proxyToken])

  async function loadProfiles() {
    if (!proxyUrl) return
    setLoading(true)
    try {
      const list = await listProfiles(proxyUrl, proxyToken)
      setProfiles(list)
    } catch {
      setProfileError('Error al cargar perfiles')
    } finally {
      setLoading(false)
    }
  }

  async function handleTest(id: string) {
    if (!proxyUrl) return
    setTestStates(s => ({ ...s, [id]: { status: 'testing' } }))
    const result = await testProfile(proxyUrl, proxyToken, id)
    setTestStates(s => ({
      ...s,
      [id]: result.success
        ? { status: 'ok', latencyMs: result.latencyMs }
        : { status: 'error', error: result.error },
    }))
  }

  async function handleActivate() {
    if (!proxyUrl || !confirmActivate) return
    setActivating(true)
    try {
      await activateProfile(proxyUrl, proxyToken, confirmActivate.id)
      setConfirmActivate(null)
      await relaunch(proxyUrl, proxyToken)
    } catch (err: any) {
      setProfileError(err.message ?? 'Error al activar perfil')
      setActivating(false)
    }
  }

  async function handleDelete() {
    if (!proxyUrl || !confirmDelete) return
    setDeleting(true)
    try {
      await deleteProfile(proxyUrl, proxyToken, confirmDelete.id)
      setConfirmDelete(null)
      await loadProfiles()
    } catch (err: any) {
      setProfileError(err.message ?? 'Error al eliminar perfil')
    } finally {
      setDeleting(false)
    }
  }

  async function testFormUrl(url: string, form: ProfileForm, setForm: (f: ProfileForm) => void) {
    if (!proxyUrl || !url.trim()) return
    setForm({ ...form, testState: { status: 'testing' } })
    const start = Date.now()
    try {
      const r = await fetch(`${proxyUrl}/local/test-connection`, {
        method: 'POST',
        headers: { ...headers, 'Content-Type': 'application/json' },
        body: JSON.stringify({ url }),
        signal: AbortSignal.timeout(6000),
      })
      const data = await r.json() as { success: boolean; error?: string }
      if (data.success) {
        setForm({ ...form, testState: { status: 'ok', latencyMs: Date.now() - start }, testedUrl: url })
      } else {
        setForm({ ...form, testState: { status: 'error', error: data.error ?? 'Error de conexión' }, testedUrl: null })
      }
    } catch {
      setForm({ ...form, testState: { status: 'error', error: 'Sin respuesta del servidor' }, testedUrl: null })
    }
  }

  async function handleAddSave(confirmed = false) {
    if (!proxyUrl || !addForm) return
    if (!confirmed && addForm.testedUrl !== addForm.databaseUrl) {
      if (!window.confirm('No has probado la conexión. ¿Guardar de todas formas?')) return
    }
    setAddForm({ ...addForm, saving: true, error: null })
    try {
      await createProfile(proxyUrl, proxyToken, addForm.name.trim(), addForm.databaseUrl)
      setAddForm(null)
      await loadProfiles()
    } catch (err: any) {
      setAddForm({ ...addForm, saving: false, error: err.message ?? 'Error al guardar' })
    }
  }

  function startEdit(profile: ClientProfile) {
    setEditingId(profile.id)
    setEditForm({ ...emptyForm(), name: profile.name })
  }

  async function handleEditSave(confirmed = false) {
    if (!proxyUrl || !editingId || !editForm) return
    const updates: { name?: string; databaseUrl?: string } = {}
    if (editForm.name.trim()) updates.name = editForm.name.trim()
    if (editForm.databaseUrl) {
      if (!confirmed && editForm.testedUrl !== editForm.databaseUrl) {
        if (!window.confirm('No has probado la nueva URL. ¿Guardar de todas formas?')) return
      }
      updates.databaseUrl = editForm.databaseUrl
    }
    setEditForm({ ...editForm, saving: true, error: null })
    try {
      await updateProfile(proxyUrl, proxyToken, editingId, updates)
      setEditingId(null)
      setEditForm(null)
      await loadProfiles()
    } catch (err: any) {
      setEditForm({ ...editForm, saving: false, error: err.message ?? 'Error al actualizar' })
    }
  }

  useEffect(() => {
    if (!showMigrations || !proxyUrl) return
    loadMigrationStatus()
  }, [showMigrations, proxyUrl])

  function loadMigrationStatus() {
    if (!proxyUrl) return
    setMigrLoading(true)
    fetch(`${proxyUrl}/local/migrations/status`, { headers })
      .then(r => r.json())
      .then((d: any) => setMigrStatus(d))
      .catch(() => {})
      .finally(() => setMigrLoading(false))
  }

  async function handleRunMigrations(opToken?: string) {
    if (!proxyUrl) return
    setRunning(true); setRunResults(null); setRunError(null)
    try {
      const extraHeaders: Record<string, string> = opToken ? { 'X-Op-Token': opToken } : {}
      const r = await fetch(`${proxyUrl}/local/migrations/run`, {
        method: 'POST', headers: { ...headers, ...extraHeaders },
      })
      if (r.status === 401 && !opToken) {
        setShowTotpModal(true)
        return
      }
      const data = await r.json() as { results?: MigrationRunEntry[]; error?: string }
      if (data.results) setRunResults(data.results)
      if (data.error) setRunError(data.error)
      loadMigrationStatus()
    } catch {
      setRunError('Error de red al ejecutar migraciones')
    } finally {
      setRunning(false)
    }
  }

  return (
    <div
      className="min-h-screen text-white flex flex-col relative"
      style={{ background: 'linear-gradient(135deg, #0c1c2e 0%, #0b2d1f 45%, #0e1a32 100%)' }}
    >
      {/* Background orbs */}
      <div className="fixed -top-40 -right-40 w-[500px] h-[500px] rounded-full blur-3xl pointer-events-none"
           style={{ background: 'radial-gradient(circle, hsl(160 84% 39% / 0.2) 0%, transparent 70%)' }} />
      <div className="fixed -bottom-40 -left-20 w-[400px] h-[400px] rounded-full blur-3xl pointer-events-none"
           style={{ background: 'radial-gradient(circle, #4f46e5 0%, transparent 70%)', opacity: 0.15 }} />

      {/* Header */}
      <div className="relative border-b border-white/8 bg-white/3 backdrop-blur-sm px-6 py-4 flex items-center gap-4">
        <button
          onClick={onBack}
          className="flex items-center gap-1.5 text-white/50 hover:text-white transition-colors text-sm px-2 py-1.5 rounded-lg hover:bg-white/8"
        >
          <ArrowLeft className="w-4 h-4" />
          Volver al login
        </button>
        <div className="h-4 w-px bg-white/10" />
        <div className="flex items-center gap-2.5">
          <div className="w-7 h-7 rounded-lg bg-white/10 border border-white/15 flex items-center justify-center">
            <span className="text-white font-bold text-xs">IS</span>
          </div>
          <span className="font-semibold text-base text-white">Modo Proveedor</span>
        </div>
        <span className="ml-auto inline-flex items-center gap-1.5 text-xs text-emerald-400 bg-emerald-500/10 border border-emerald-500/20 px-2.5 py-1 rounded-full font-medium">
          <div className="w-1.5 h-1.5 rounded-full bg-emerald-400" />
          Satelab
        </span>
      </div>

      <div className="relative flex-1 overflow-auto">
        <div className="max-w-2xl mx-auto px-6 py-8 flex flex-col gap-8">

          {/* Profiles section */}
          <div>
            <div className="flex items-center justify-between mb-4">
              <div>
                <h2 className="text-white font-semibold text-base">Perfiles de cliente</h2>
                <p className="text-white/35 text-xs mt-0.5">Conexiones a bases de datos configuradas</p>
              </div>
              <button
                onClick={() => setAddForm(emptyForm())}
                className="flex items-center gap-1.5 text-xs font-medium text-white px-3 py-2 rounded-lg border border-white/15 hover:bg-white/8 transition-all"
              >
                <Plus className="w-3.5 h-3.5" />
                Agregar cliente
              </button>
            </div>

            {profileError && (
              <div className="flex items-center gap-2 text-red-400 text-xs bg-red-500/10 border border-red-500/20 rounded-lg px-3 py-2 mb-3">
                <XCircle className="w-3.5 h-3.5 shrink-0" />
                {profileError}
              </div>
            )}

            {loading ? (
              <div className="flex justify-center py-12">
                <Loader2 className="w-5 h-5 animate-spin text-white/25" />
              </div>
            ) : profiles.length === 0 && !addForm ? (
              <div className="border border-dashed border-white/10 rounded-2xl p-10 text-center">
                <div className="w-12 h-12 rounded-2xl bg-white/5 border border-white/10 flex items-center justify-center mx-auto mb-4">
                  <Plus className="w-5 h-5 text-white/30" />
                </div>
                <p className="text-white/40 text-sm mb-4">No hay perfiles configurados</p>
                <button
                  onClick={() => setAddForm(emptyForm())}
                  className="text-xs font-medium text-white/60 hover:text-white border border-white/15 hover:border-white/30 px-3 py-1.5 rounded-lg transition-all"
                >
                  + Agregar el primer cliente
                </button>
              </div>
            ) : (
              <div className="flex flex-col gap-2.5">
                {profiles.map(profile => (
                  <ProfileCard
                    key={profile.id}
                    profile={profile}
                    testState={testStates[profile.id] ?? { status: 'idle' }}
                    isEditing={editingId === profile.id}
                    editForm={editingId === profile.id ? editForm : null}
                    onActivate={() => setConfirmActivate(profile)}
                    onTest={() => handleTest(profile.id)}
                    onEdit={() => startEdit(profile)}
                    onDelete={() => setConfirmDelete(profile)}
                    onEditFormChange={setEditForm}
                    onTestUrl={(url) => editForm && testFormUrl(url, editForm, setEditForm)}
                    onEditSave={handleEditSave}
                    onEditCancel={() => { setEditingId(null); setEditForm(null) }}
                    isOnlyProfile={profiles.length === 1}
                  />
                ))}
              </div>
            )}

            {addForm && (
              <ProfileFormCard
                form={addForm}
                title="Nuevo perfil"
                onChange={setAddForm}
                onTest={(url) => testFormUrl(url, addForm, setAddForm)}
                onSave={() => handleAddSave()}
                onCancel={() => setAddForm(null)}
              />
            )}
          </div>

          {/* Migrations section */}
          <CollapsibleSection
            icon={<ArrowRightLeft className="w-4 h-4" />}
            title="Migraciones"
            open={showMigrations}
            onToggle={() => setShowMigrations(v => !v)}
          >
            <div className="flex flex-col gap-4 pt-2">
              {migrLoading ? (
                <div className="flex justify-center py-6">
                  <Loader2 className="w-4 h-4 animate-spin text-white/25" />
                </div>
              ) : migrStatus && (
                <>
                  {/* Auto-applied notice */}
                  {migrStatus.pending === 0 && migrStatus.applied > 0 && migrStatus.lastAppliedAt && (
                    <div className="flex items-start gap-2 bg-emerald-500/8 border border-emerald-500/20 rounded-xl px-3 py-2.5">
                      <CheckCircle className="w-3.5 h-3.5 text-emerald-400 shrink-0 mt-0.5" />
                      <div>
                        <p className="text-emerald-400 text-xs font-medium">Base de datos al día</p>
                        <p className="text-white/35 text-[10px] mt-0.5">
                          {migrStatus.applied} migraciones aplicadas automáticamente al iniciar la app ·{' '}
                          última: {new Date(migrStatus.lastAppliedAt).toLocaleString('es-CO', {
                            day: '2-digit', month: '2-digit', year: '2-digit',
                            hour: '2-digit', minute: '2-digit',
                          })}
                        </p>
                      </div>
                    </div>
                  )}
                  {migrStatus.pending > 0 && (
                    <div className="flex items-start gap-2 bg-amber-500/8 border border-amber-500/20 rounded-xl px-3 py-2.5">
                      <div className="w-3.5 h-3.5 rounded-full border border-amber-400 shrink-0 mt-0.5" />
                      <div>
                        <p className="text-amber-400 text-xs font-medium">{migrStatus.pending} migración{migrStatus.pending !== 1 ? 'es' : ''} pendiente{migrStatus.pending !== 1 ? 's' : ''}</p>
                        <p className="text-white/35 text-[10px] mt-0.5">
                          Ejecuta las migraciones para actualizar la estructura de la base de datos.
                        </p>
                      </div>
                    </div>
                  )}

                  <div className="flex gap-3">
                    <div className="flex-1 bg-white/5 border border-white/8 rounded-xl px-4 py-3 text-center">
                      <p className="text-2xl font-bold text-white">{migrStatus.applied}</p>
                      <p className="text-xs text-white/35 mt-0.5">Aplicadas</p>
                    </div>
                    <div className={`flex-1 rounded-xl px-4 py-3 text-center border ${
                      migrStatus.pending > 0
                        ? 'bg-amber-500/10 border-amber-500/20'
                        : 'bg-white/5 border-white/8'
                    }`}>
                      <p className={`text-2xl font-bold ${migrStatus.pending > 0 ? 'text-amber-400' : 'text-white'}`}>
                        {migrStatus.pending}
                      </p>
                      <p className="text-xs text-white/35 mt-0.5">Pendientes</p>
                    </div>
                  </div>
                  <div className="max-h-40 overflow-y-auto flex flex-col gap-0.5">
                    {migrStatus.migrations.map(m => (
                      <div key={m.name} className="flex items-center gap-2.5 text-xs py-1.5 px-1">
                        {m.status === 'applied'
                          ? <CheckCircle className="w-3.5 h-3.5 text-emerald-500 shrink-0" />
                          : <div className="w-3.5 h-3.5 rounded-full border border-amber-500/60 shrink-0" />
                        }
                        <span className={`font-mono truncate ${m.status === 'applied' ? 'text-white/50' : 'text-white/80'}`}>
                          {m.name}
                        </span>
                        {m.appliedAt && (
                          <span className="text-white/25 ml-auto shrink-0">
                            {new Date(m.appliedAt).toLocaleDateString()}
                          </span>
                        )}
                      </div>
                    ))}
                  </div>
                </>
              )}
              {runResults && (
                <div className="flex flex-col gap-0.5 bg-white/3 rounded-xl p-3">
                  {runResults.map(r => (
                    <div key={r.name} className={`flex items-center gap-2 text-xs py-1 ${r.status === 'failed' ? 'text-red-400' : 'text-white/50'}`}>
                      {r.status === 'applied'  && <CheckCircle className="w-3.5 h-3.5 text-emerald-500 shrink-0" />}
                      {r.status === 'skipped'  && <span className="w-3.5 shrink-0 text-center">↷</span>}
                      {r.status === 'failed'   && <XCircle className="w-3.5 h-3.5 shrink-0" />}
                      <span className="font-mono truncate">{r.name}</span>
                      {r.error && <span className="text-red-400 ml-2 truncate">{r.error}</span>}
                    </div>
                  ))}
                </div>
              )}
              {runError && (
                <div className="flex items-center gap-2 text-red-400 text-xs bg-red-500/10 border border-red-500/20 rounded-lg px-3 py-2">
                  <XCircle className="w-3.5 h-3.5 shrink-0" />
                  {runError}
                </div>
              )}
              <button
                onClick={() => handleRunMigrations()}
                disabled={running}
                className="w-full flex items-center justify-center gap-2 py-2.5 rounded-xl text-white text-sm font-semibold transition-all active:scale-[0.98] disabled:opacity-40 disabled:cursor-not-allowed"
                style={{ background: 'linear-gradient(135deg, #2563eb, #4f46e5)' }}
              >
                {running ? <Loader2 className="w-4 h-4 animate-spin" /> : <ArrowRightLeft className="w-4 h-4" />}
                {running ? 'Ejecutando...' : 'Ejecutar migraciones'}
              </button>
            </div>
          </CollapsibleSection>
        </div>
      </div>

      {/* Confirm activate dialog */}
      {confirmActivate && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 backdrop-blur-sm p-4">
          <div className="bg-[#0f1e30] border border-white/10 rounded-2xl p-6 w-full max-w-sm shadow-2xl">
            <h3 className="text-white font-semibold text-base mb-2">¿Cambiar a {confirmActivate.name}?</h3>
            <p className="text-white/50 text-sm mb-6 leading-relaxed">
              La app se reiniciará para aplicar el cambio. El proceso toma unos segundos.
            </p>
            <div className="flex gap-2.5">
              <button
                onClick={() => setConfirmActivate(null)}
                className="flex-1 border border-white/12 text-white/60 hover:text-white hover:bg-white/8 text-sm py-2.5 rounded-xl transition-all"
              >
                Cancelar
              </button>
              <button
                onClick={handleActivate}
                disabled={activating}
                className="flex-1 flex items-center justify-center gap-1.5 text-white text-sm font-semibold py-2.5 rounded-xl transition-all active:scale-[0.98] disabled:opacity-40"
                style={{ background: 'linear-gradient(135deg, #2563eb, #4f46e5)' }}
              >
                {activating && <Loader2 className="w-3.5 h-3.5 animate-spin" />}
                Cambiar y reiniciar
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Confirm delete dialog */}
      {confirmDelete && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 backdrop-blur-sm p-4">
          <div className="bg-[#0f1e30] border border-white/10 rounded-2xl p-6 w-full max-w-sm shadow-2xl">
            <h3 className="text-white font-semibold text-base mb-2">¿Eliminar {confirmDelete.name}?</h3>
            <p className="text-white/50 text-sm mb-6">Esta acción no se puede deshacer.</p>
            <div className="flex gap-2.5">
              <button
                onClick={() => setConfirmDelete(null)}
                className="flex-1 border border-white/12 text-white/60 hover:text-white hover:bg-white/8 text-sm py-2.5 rounded-xl transition-all"
              >
                Cancelar
              </button>
              <button
                onClick={handleDelete}
                disabled={deleting}
                className="flex-1 flex items-center justify-center gap-1.5 bg-red-500/15 border border-red-500/30 hover:bg-red-500/25 text-red-400 hover:text-red-300 text-sm font-semibold py-2.5 rounded-xl transition-all active:scale-[0.98] disabled:opacity-40"
              >
                {deleting && <Loader2 className="w-3.5 h-3.5 animate-spin" />}
                Eliminar
              </button>
            </div>
          </div>
        </div>
      )}

      {showTotpModal && (
        <TotpVerifyModal
          actionLabel="Verificar y ejecutar"
          onVerified={(opToken) => { setShowTotpModal(false); handleRunMigrations(opToken) }}
          onCancel={() => setShowTotpModal(false)}
        />
      )}
    </div>
  )
}

// ── Sub-components ──────────────────────────────────────────────────────────

interface ProfileCardProps {
  profile: ClientProfile
  testState: TestState
  isEditing: boolean
  editForm: ProfileForm | null
  isOnlyProfile: boolean
  onActivate: () => void
  onTest: () => void
  onEdit: () => void
  onDelete: () => void
  onEditFormChange: (f: ProfileForm) => void
  onTestUrl: (url: string) => void
  onEditSave: () => void
  onEditCancel: () => void
}

function ProfileCard({
  profile, testState, isEditing, editForm, isOnlyProfile,
  onActivate, onTest, onEdit, onDelete,
  onEditFormChange, onTestUrl, onEditSave, onEditCancel,
}: ProfileCardProps) {
  return (
    <div className={`border rounded-2xl p-4 transition-all ${
      profile.isActive
        ? 'border-emerald-500/30 bg-emerald-500/8'
        : 'border-white/8 bg-white/4 hover:bg-white/6'
    }`}>
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2 mb-1">
            <span className="font-semibold text-sm text-white truncate">{profile.name}</span>
            {profile.isActive && (
              <span className="inline-flex items-center gap-1 text-[10px] bg-emerald-500/15 text-emerald-400 border border-emerald-500/25 px-2 py-0.5 rounded-full font-medium shrink-0">
                <div className="w-1 h-1 rounded-full bg-emerald-400" />
                Activo
              </span>
            )}
          </div>
          {profile.databaseUrlPreview && (
            <span className="font-mono text-xs text-white/30">{profile.databaseUrlPreview}</span>
          )}
          <div className="mt-1 text-xs text-white/20">
            Último uso: {profile.lastUsedAt ? new Date(profile.lastUsedAt).toLocaleDateString() : '—'}
          </div>
        </div>
        <div className="flex items-center gap-1 shrink-0">
          <button
            onClick={onTest}
            disabled={testState.status === 'testing'}
            title="Probar conexión"
            className="p-2 text-white/30 hover:text-blue-400 hover:bg-blue-500/10 rounded-lg transition-all disabled:opacity-30"
          >
            {testState.status === 'testing'
              ? <Loader2 className="w-3.5 h-3.5 animate-spin" />
              : <Signal className="w-3.5 h-3.5" />
            }
          </button>
          <button
            onClick={onEdit}
            title="Editar"
            className="p-2 text-white/30 hover:text-white/80 hover:bg-white/8 rounded-lg transition-all"
          >
            <Pencil className="w-3.5 h-3.5" />
          </button>
          <button
            onClick={onDelete}
            disabled={isOnlyProfile && profile.isActive}
            title={isOnlyProfile && profile.isActive ? 'No puedes eliminar el único perfil activo' : 'Eliminar'}
            className="p-2 text-white/30 hover:text-red-400 hover:bg-red-500/10 rounded-lg transition-all disabled:opacity-20 disabled:cursor-not-allowed"
          >
            <Trash2 className="w-3.5 h-3.5" />
          </button>
          {!profile.isActive && (
            <button
              onClick={onActivate}
              className="ml-1 text-xs font-medium text-white px-3 py-1.5 rounded-lg transition-all active:scale-[0.97]"
              style={{ background: 'linear-gradient(135deg, #2563eb, #4f46e5)' }}
            >
              Activar
            </button>
          )}
        </div>
      </div>

      {testState.status === 'ok' && (
        <div className="flex items-center gap-1.5 text-emerald-400 text-xs mt-2.5 bg-emerald-500/10 border border-emerald-500/20 rounded-lg px-2.5 py-1.5">
          <CheckCircle className="w-3 h-3" />
          Conexión exitosa{testState.latencyMs ? ` · ${testState.latencyMs}ms` : ''}
        </div>
      )}
      {testState.status === 'error' && (
        <div className="flex items-center gap-1.5 text-red-400 text-xs mt-2.5 bg-red-500/10 border border-red-500/20 rounded-lg px-2.5 py-1.5">
          <XCircle className="w-3 h-3" />
          {testState.error ?? 'Error de conexión'}
        </div>
      )}

      {isEditing && editForm && (
        <div className="mt-4 pt-4 border-t border-white/8">
          <ProfileFormCard
            form={editForm}
            title="Editar perfil"
            compact
            onChange={onEditFormChange}
            onTest={onTestUrl}
            onSave={onEditSave}
            onCancel={onEditCancel}
          />
        </div>
      )}
    </div>
  )
}

interface ProfileFormCardProps {
  form: ProfileForm
  title: string
  compact?: boolean
  onChange: (f: ProfileForm) => void
  onTest: (url: string) => void
  onSave: () => void
  onCancel: () => void
}

function ProfileFormCard({ form, title, compact, onChange, onTest, onSave, onCancel }: ProfileFormCardProps) {
  const wrapClass = compact ? '' : 'border border-white/10 bg-white/4 rounded-2xl p-4 mt-2.5'
  return (
    <div className={wrapClass}>
      {!compact && <p className="text-sm font-semibold text-white mb-4">{title}</p>}
      <div className="flex flex-col gap-3">
        <div className="flex flex-col gap-1.5">
          <label className="text-white/40 text-xs font-medium uppercase tracking-wide">Nombre del cliente</label>
          <input
            type="text"
            value={form.name}
            onChange={e => onChange({ ...form, name: e.target.value })}
            placeholder="Ej: Gym Norte"
            className="bg-white/5 border border-white/10 rounded-xl px-3 py-2.5 text-sm text-white placeholder:text-white/20 focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-500/40 focus-visible:ring-offset-1 focus-visible:ring-offset-transparent transition-all"
          />
        </div>
        <div className="flex flex-col gap-1.5">
          <label className="text-white/40 text-xs font-medium uppercase tracking-wide">URL de base de datos</label>
          <div className="relative">
            <input
              type={form.showUrl ? 'text' : 'password'}
              value={form.databaseUrl}
              onChange={e => onChange({ ...form, databaseUrl: e.target.value, testState: { status: 'idle' }, testedUrl: null })}
              placeholder="postgresql://user:pass@host:5432/db"
              className="w-full bg-white/5 border border-white/10 rounded-xl px-3 py-2.5 pr-9 text-sm text-white placeholder:text-white/20 focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-500/40 focus-visible:ring-offset-1 focus-visible:ring-offset-transparent font-mono transition-all"
            />
            <button
              type="button"
              onClick={() => onChange({ ...form, showUrl: !form.showUrl })}
              className="absolute right-3 top-1/2 -translate-y-1/2 text-white/30 hover:text-white/70 transition-colors"
            >
              {form.showUrl ? <EyeOff className="w-3.5 h-3.5" /> : <Eye className="w-3.5 h-3.5" />}
            </button>
          </div>
        </div>

        {form.testState.status === 'ok' && (
          <div className="flex items-center gap-1.5 text-emerald-400 text-xs bg-emerald-500/10 border border-emerald-500/20 rounded-lg px-2.5 py-1.5">
            <CheckCircle className="w-3 h-3" />
            Conexión exitosa{form.testState.latencyMs ? ` · ${form.testState.latencyMs}ms` : ''}
          </div>
        )}
        {form.testState.status === 'error' && (
          <div className="flex items-center gap-1.5 text-red-400 text-xs bg-red-500/10 border border-red-500/20 rounded-lg px-2.5 py-1.5">
            <XCircle className="w-3 h-3" />
            {form.testState.error}
          </div>
        )}
        {form.error && (
          <p className="text-red-400 text-xs bg-red-500/10 border border-red-500/20 rounded-lg px-2.5 py-1.5">{form.error}</p>
        )}

        <div className="flex gap-2 pt-1">
          <button
            onClick={() => onTest(form.databaseUrl)}
            disabled={form.testState.status === 'testing' || !form.databaseUrl.trim()}
            className="flex-1 flex items-center justify-center gap-1.5 border border-white/12 text-white/60 hover:text-white hover:bg-white/8 disabled:opacity-30 text-xs font-medium py-2 rounded-xl transition-all"
          >
            {form.testState.status === 'testing'
              ? <Loader2 className="w-3 h-3 animate-spin" />
              : <Signal className="w-3 h-3" />
            }
            {form.testState.status === 'testing' ? 'Probando...' : 'Probar'}
          </button>
          <button
            onClick={onSave}
            disabled={form.saving || !form.name.trim()}
            className="flex-1 flex items-center justify-center gap-1.5 text-white text-xs font-semibold py-2 rounded-xl transition-all active:scale-[0.97] disabled:opacity-40"
            style={{ background: 'linear-gradient(135deg, #2563eb, #4f46e5)' }}
          >
            {form.saving && <Loader2 className="w-3 h-3 animate-spin" />}
            Guardar
          </button>
          <button
            onClick={onCancel}
            className="px-3 text-white/30 hover:text-white/70 text-xs transition-colors"
          >
            Cancelar
          </button>
        </div>
      </div>
    </div>
  )
}

interface CollapsibleSectionProps {
  icon: React.ReactNode
  title: string
  open: boolean
  onToggle: () => void
  children: React.ReactNode
}

function CollapsibleSection({ icon, title, open, onToggle, children }: CollapsibleSectionProps) {
  return (
    <div className={`border rounded-2xl overflow-hidden transition-all ${
      open ? 'border-white/15 bg-white/4' : 'border-white/8 bg-white/3 hover:bg-white/5'
    }`}>
      <button
        onClick={onToggle}
        className="w-full flex items-center justify-between px-5 py-4 transition-colors"
      >
        <div className="flex items-center gap-2.5 text-white/70">
          {icon}
          <span className="text-sm font-medium">{title}</span>
        </div>
        {open
          ? <ChevronUp className="w-4 h-4 text-white/30" />
          : <ChevronDown className="w-4 h-4 text-white/30" />
        }
      </button>
      {open && (
        <div className="px-5 pb-5 border-t border-white/8">
          {children}
        </div>
      )}
    </div>
  )
}
